import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core"
import * as d3 from "d3"
import { sleepForMS } from "../../helpers/helpers"
import { CommonModule } from "@angular/common"
import { JoiInputComponent } from "../joi-input/joi-input.component"
import { SmallButtonComponent } from "../buttons/small-button.component"

const len = 300 // can't change or change to width and height.  Tried extensively and couldn't get it to work without doing thorough research into HSL
const scales = ({
  h: d3.scaleLinear().domain([0, len]).range([360, 0]).clamp(true),
  l2: d3.scaleLinear().domain([0, len]).range([1, 0]),
  l1: d3.scaleLinear().domain([0, len]).range([1, 0.5]),
  s: d3.scaleLinear().domain([0, len]).range([0, 1])
})
@Component({
    selector: 'color-picker',
    template: `

<div class="gui">
  <div class="picker">
    <canvas #picker></canvas>
  </div>
  <div class="spectrum">
    <canvas #spectrum></canvas>
  </div>
</div>
<div class="controls">
  <div class="colors">
    <div class="color-box" [style.background-color]="previousColor" (click)="revertToPrevious()"></div>
    <div class="color-box" [ngStyle]="{ backgroundColor: currentColor }"></div>
  </div>
  <div style="flex:1;"></div>
  <label class="hexLabel">Hex: </label>
  <joi-input class="hexInput"
    [(model)]="hexHolderColor" 
    [fixedWidth]="true"
    [placeholder]="'Hex'"
    [outlined]="true"
    [allowableCharacters]="'0123456789#abcdefABCDEF'"
    (joiChange)="updateFromHex()"
  ></joi-input>
</div>
<div class="rgb">
  <small-button mat-button (click)="changeColor.emit()">Cancel</small-button>
  <div style="flex:1;"></div>
  <label class="rgbLabel">R:</label>
  <joi-input class="rgbInput"
    [(model)]="R" 
    [fixedWidth]="true"
    [placeholder]="'0'"
    [outlined]="true"
    [allowableCharacters]="'0123456789.'"
    (joiChange)="updateFromRGBA()"
  ></joi-input>
  <div style="flex:1;"></div>
  <label class="rgbLabel">G:</label>
  <joi-input class="rgbInput"
    [(model)]="G" 
    [fixedWidth]="true"
    [placeholder]="'0'"
    [outlined]="true"
    [allowableCharacters]="'0123456789.'"
    (joiChange)="updateFromRGBA()"
  ></joi-input>
  <div style="flex:1;"></div>
  <label class="rgbLabel">B:</label>
  <joi-input class="rgbInput"
    [(model)]="B" 
    [fixedWidth]="true"
    [placeholder]="'0'"
    [outlined]="true"
    [allowableCharacters]="'0123456789.'"
    (joiChange)="updateFromRGBA()"
  ></joi-input>
  <div style="flex:1;"></div>
  <small-button (click)="changeColor.emit(currentColor)">OK</small-button>
</div>
<div class="buttons">
  
  
</div>

`,
    styles: [`
:host { display: flex; flex-direction: column; width: fit-content; background: white; border: 1px solid #ddd; padding: 10px 10px 0; }
.controls { display: flex; width: 100%; margin: 5px 0px 0px; }
.colors { display: flex; width: calc(100% - 5px); margin: 0px 1px; }
.rgbInput { width: 40px; margin-top: -1px }
.rgb { display: flex;  width: 100%; margin: 5px 0 0;  }
.hexInput { width: 100px; margin-right: 1px; }
.hexLabel, .rgbLabel { margin-right: 5px; }
.color-box { width: 50px; height: 25px; }
.buttons { display: flex; margin: 5px 0 0; }
canvas { height: 100%; width: 100%; }
.picker { 
  margin: 0px;
  cursor: crosshair;
  border: 1px solid darkgrey;
  padding: 1px 1px 0.5px;
}
.spectrum {
  width: 29px;
  height: 303px;
  margin-left: 5px;
  cursor: crosshair;
  border: 1px solid darkgrey;
  padding: 1px;
}
.gui {
  width: 338px;
  display: flex;
  flex-wrap: wrap;
}
`],
    standalone: true,
    imports: [CommonModule, JoiInputComponent, SmallButtonComponent]
})
export class ColorPickerComponent implements OnInit {
  @Input() color: string
  @ViewChild('wrapper') wrapper: ElementRef<HTMLDivElement>
  @ViewChild('picker') picker: ElementRef<HTMLCanvasElement>
  @ViewChild('spectrum') spectrum: ElementRef<HTMLCanvasElement>
  @Output() changeColor: EventEmitter<string> = new EventEmitter()

  hexHolderColor: string
  currentColor: string
  previousColor: string
  len = len
  coords = [len, 0]
  R: number
  G: number
  B: number
  A: number
  hue = 210

  updateCurrentColor(hex: string, andHue?: boolean) {
    console.log({ hex })
    if (hex[0] !== '#') hex = `#${hex}`
    console.log({ hex })
    this.previousColor = this.currentColor || this.color
    this.currentColor = hex
    this.hexHolderColor = hex
    const { r, g, b, opacity } = d3.rgb(hex)
    if (andHue) this.hue = d3.hsl(this.color).h
    this.R = r
    this.G = g
    this.B = b
    this.A = opacity
    d3.select(this.picker.nativeElement).call(this.renderPicker.bind(this))
  }

  revertToPrevious() {
    this.updateCurrentColor(this.previousColor)
  }

  updateFromHex() {
    if (/^#?([0-9A-Fa-f]{3,6})$/i.test(this.hexHolderColor)) {
      this.updateCurrentColor(this.hexHolderColor)
    } else {
      this.hexHolderColor = this.currentColor
    }
  }

  getHSL() {
    return d3.hsl(this.hue, scales.s(this.coords[0]), scales.l1(this.coords[0]) * scales.l2(this.coords[1]))
  }

  updateFromRGBA() {
    const { R, G, B, A } = this
    const rgb = d3.rgb(R, G, B, A)
    const hex = rgb.formatHex()
    this.updateCurrentColor(hex, true)
  }
  
  renderPicker(g: any) {
    let i = -1;

    const current = this.getHSL()
    const ctx = g.node().getContext('2d')
    const imageData = ctx.createImageData(len, len)
    
    for (let y = 0; y < len; ++y) {
      for (let x = 0, c; x < len; ++x) {
        current.s = scales.s(x);
        current.l = scales.l1(x) * scales.l2(y);
        c = current.rgb();
        imageData.data[++i] = c.r;
        imageData.data[++i] = c.g;
        imageData.data[++i] = c.b;
        imageData.data[++i] = 255;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }

  renderSpectrum(g: any) {
    let i = -1;
    
    const current = this.getHSL()
    const ctx = g.node().getContext('2d')
    const imageData = ctx.createImageData(1, len);

    for (let y = 0, c; y <= len; ++y) {
      current.h = scales.h(y);
      c = current.rgb();
      imageData.data[++i] = c.r;
      imageData.data[++i] = c.g;
      imageData.data[++i] = c.b;
      imageData.data[++i] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
  }

  colorDrag(dragEvent: DragEvent) {
    const { x,y } = dragEvent
    this.coords = [ Math.max(Math.min(x, len), 0), Math.max(Math.min(y, len), 0) ]
    this.updateCurrentColor(this.getHSL().formatHex())
  }

  spectrumDrag(dragEvent: DragEvent) {
    this.hue = scales.h(dragEvent.y)
    d3.select(this.picker.nativeElement).call(this.renderPicker.bind(this))
    this.updateCurrentColor(this.getHSL().formatHex())
  }

  async ngOnInit() {
    await sleepForMS(0)


    this.updateCurrentColor(this.color, true)
    d3.select(this.picker.nativeElement)
      .call(this.renderPicker.bind(this))
      .call((d3 as any).drag()
        .on('start', this.colorDrag.bind(this))
        .on('drag', this.colorDrag.bind(this)))

    d3.select(this.spectrum.nativeElement)
      .attr('width', 1)
      .attr('height', `${len}`)
      .style('width', '25px')
      .style('height', `${len}px`)
      .call(this.renderSpectrum.bind(this))
      .call((d3 as any).drag()
        .on('start', this.spectrumDrag.bind(this))
        .on('drag', this.spectrumDrag.bind(this)))
 
  } 



}

